import { ReactNode } from 'react'

import clsx from 'clsx'

import { ChildProps } from '@/common/util/props'

import Icon, { IconColor, IconType } from '@/components/view/atoms/icon/Icon'
import ModuleIcon, {
  ModuleIconColor,
  ModuleIconType
} from '@/components/view/atoms/icon/ModuleIcon'
import LoadingObject, { LoadingObjectType } from '@/components/view/atoms/loading/LoadingObject'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import InternalLink from '@/components/view/utils/link/InternalLink'
import {
  NavigationColor,
  navColorClass,
  navColorSelectedClass
} from '@/components/view/utils/nav/navigation'

interface Props extends ChildProps {
  link?: string
  icon?: IconType
  selected?: boolean
  viewing?: boolean
  color?: NavigationColor
  isLoading?: boolean
  onClick?: () => void
  subTitle?: ModuleIconType
  isOnlyDisplaying?: boolean
  subNode?: ReactNode
}

const NavigationRow = ({
  children,
  link,
  icon,
  selected = false,
  viewing = false,
  color = NavigationColor.ORANGE,
  isLoading = false,
  onClick,
  subTitle,
  isOnlyDisplaying = false,
  subNode
}: Props) => {
  const className = clsx(
    'w-full outline-default relative flex items-center gap-x-1 focus-visible:outline-offset-0 transition ease-in-out',
    selected ? 'rounded-tr-lg' : 'rounded-r-lg',
    viewing
      ? 'bg-fill-primary'
      : selected
        ? color
          ? navColorSelectedClass(color)
          : 'bg-fill-primary'
        : color
          ? 'bg-surface-transparent hover:bg-fill-transparent-hover active:bg-fill-transparent-active'
          : 'bg-fill-on-bg-fill',
    !(viewing || selected) && 'hover:bg-fill-on-bg-fill-hover active:bg-fill-on-bg-fill-active',
    icon ? 'py-3 pl-3 pr-2' : 'py-3 pl-9 pr-2',
    isOnlyDisplaying && 'pointer-events-none'
  )

  const Content = () => (
    <>
      {viewing && (
        <span className={clsx('absolute left-1 block h-8.75 w-1', navColorClass(color))} />
      )}
      {icon && <Icon type={icon} color={selected ? IconColor.ON_COLOR : IconColor.SUMI} />}
      <Text
        size={14}
        color={selected && color ? TextColor.ON_BG_FILL : TextColor.PRIMARY}
        lineHeight={120}
        weight={selected || viewing ? 'bold' : 'normal'}
      >
        {children}
      </Text>
      {subTitle && (selected || viewing) && (
        <span className='ml-auto'>
          {/* <Text size={12} color={TextColor.ON_BG_FILL} lineHeight={120}>
            {subTitle}
          </Text> */}
          <ModuleIcon
            type={subTitle}
            color={selected ? ModuleIconColor.ON_COLOR : ModuleIconColor.PRIMARY}
          />
        </span>
      )}
    </>
  )

  return (
    <div className='relative'>
      {isLoading ? (
        <div className={'outline-default flex items-center gap-x-2 p-3'}>
          <Icon
            type={IconType.LOADING}
            color={selected ? IconColor.PRIMARY : IconColor.SECONDARY}
          />
          <LoadingObject type={LoadingObjectType.TEXT_SHORT} color='secondary' />
        </div>
      ) : link ? (
        <InternalLink link={link} className={className} onClick={onClick}>
          <Content />
        </InternalLink>
      ) : (
        <button className={className} onClick={onClick}>
          <Content />
        </button>
      )}
      {subNode && (
        <span className='absolute top-1/2 right-0 -translate-y-1/2 transform pr-2'>{subNode}</span>
      )}
    </div>
  )
}

export default NavigationRow
